/* PIPE-INST T4 (firma #118) — LA GRAMÁTICA DE LA PANTALLA DEL PROFESOR.
 *
 * ── QUÉ ERA ESTE ARCHIVO Y QUÉ ES DESDE LA TANDA 26 (firma #234) ────────────
 *
 * Nació guardando VEINTISIETE COPYS EN ESPAÑOL DURO, con una razón firmada: «la
 * superficie de profesor del piloto es de un solo idioma, y una llave de i18n con
 * una sola traducción es una promesa de segundo idioma que nadie ha hecho». El
 * propio comentario decía cómo terminaría: «el día que exista el segundo, estos
 * textos se mudan al árbol de una vez — no de a uno, según se vayan tocando».
 *
 * Ese día llegó. Los veintisiete se fueron a `dashboard:groups.bank.temas.*` (y
 * los tres del chip del documento, a `groups.bank.doc.*`), y lo que queda aquí
 * es LA GRAMÁTICA: las funciones que deciden qué forma pedirle al locale. No
 * desaparecen y no se disuelven en el JSX por tres razones:
 *
 *   1. Diecisiete archivos de prueba importan de aquí. Borrar el módulo
 *      convertiría una tanda de traducción en una de reescritura de pruebas.
 *   2. El singular y el plural son una REGLA DEL PRODUCTO —«1 página dañada» y
 *      no «1 páginas dañadas»— y una regla se prueba sola. Un ternario escondido
 *      en el marcado se prueba montando la pantalla en seis estados.
 *   3. La casa ya tiene el patrón: `lib/groups.js` expone `memberCountLabel(t, n)`,
 *      `codeExpiryLabel(...)`, `joinedAtLabel(...)`. Se recibe `t` como PRIMER
 *      argumento y el módulo compone.
 *
 * LO QUE NO ES TEXTO SE QUEDA TAL CUAL: `ESTADOS_OCUPADOS`, `sePuedeMarcar`,
 * `PIPELINE_INSTITUCIONAL` y `tienePantallaDeTemas` son códigos y compuertas, no
 * copys, y no tienen nada que traducir.
 *
 * INFRA-1 (#353 d): este módulo estrena su primera bifurcación por build. La
 * regla de coherencia web/app (v240 §1) exige que todo texto pensado para la web
 * nazca con su equivalente de la app decidido en la misma firma, y la app no
 * tiene pestañas que cerrar. Es un `import` de `lib/buildTarget`, que no carga
 * nada: son dos constantes leídas del entorno de compilación.
 */
import { IS_APP_BUILD } from './buildTarget';

/* El chip del documento en la lista del BANCO (no el del tema).
 *
 * TRES ESTADOS Y NO CUATRO. `partial` cae en «Error» porque en el carril
 * institucional no existe —la generación es por tema— así que sólo lo llevan los
 * dos documentos heredados del pipeline del consumidor, que quedaron ahí SIN
 * generar nada. Pintarlos «Listo» sería repetir la mentira que la investigación
 * B-2 documentó como BUG #3. */
export function chipDeDocumento(t, status, doc) {
  /* T6 (firma #286) — UNA GRABACIÓN DICE EN QUÉ PARTE VA, Y HACE FALTA.
   *
   * `status` sigue siendo `processing` en los dos casos —el vigilante global y la
   * barra de progreso cuelgan de ese valor y no se tocan— pero para el profesor
   * NO son lo mismo: las transcripciones corren DE UNA EN UNA, así que un audio
   * recién subido puede pasar un buen rato esperando turno. Sin esto, tres audios
   * encolados dicen los tres «Procesando…» y ninguno explica por qué no avanzan.
   *
   * El servidor manda el estado en `pi_audio_estado` (`en_fila` o `transcribiendo`)
   * y lo RETIRA cuando la transcripción termina: de ahí en adelante el documento es
   * texto como cualquier otro y este chip vuelve al de siempre, sin ramas. */
  if (status === 'processing') {
    const audio = doc?.pi_audio_estado;
    /* INFRA-1 (#353 d) — EL CHIP DEJA DE INVENTARSE UNA CAUSA.
     *
     * Decía «En fila. Empieza cuando termine la grabación anterior», y en el
     * smoke de #352 lo dijo con CERO grabaciones anteriores: lo que corría era un
     * documento en «Generando». El censo (C6) lo confirmó: `_ceder_si_hay_otro`
     * cede ante CUALQUIER trabajo del motor, no sólo ante otra transcripción, y
     * `pi_audio_estado: en_fila` es un sello de NACIMIENTO — se estampa al
     * insertar el documento, antes de que nadie haya mirado si hay cola.
     *
     * El texto nuevo dice lo único que el servidor sabe de verdad («hay que
     * esperar un lugar libre») y no lo que no sabe («por qué»). El campo con la
     * causa real es una firma futura; hasta entonces, callar es más honesto que
     * adivinar. Y el final cambia por build: en la app no hay pestaña que cerrar. */
    if (audio === 'en_fila') {
      return t(IS_APP_BUILD
        ? 'groups.bank.audio.chip_queued_app'
        : 'groups.bank.audio.chip_queued');
    }
    if (audio === 'transcribiendo') return t('groups.bank.audio.chip_transcribing');
    return t('groups.bank.doc.chip_processing');
  }
  if (status === 'ready') return t('groups.bank.doc.chip_ready');
  return t('groups.bank.doc.chip_error');
}

/* EL TEXTO DEL CHIP DE UN TEMA, COMPUESTO EN UN SOLO SITIO.
 *
 * Vivía en `GroupBankTemasPage.js` y se muda aquí en la TANDA 26 por la misma
 * regla que el resto: es una función de estado a texto, y las funciones de texto
 * viven en el módulo de copys, donde se prueban sin montar la pantalla.
 *
 * OCHO ESTADOS. Los cinco de #118, los dos que estrenó T5 —«Revisado» y
 * «Publicado»—, que desde T5.2 son constantes («Publicado» perdió su `#{k}`
 * porque el número no significa nada mirando un tema), y el de INFRA-2.
 *
 * INFRA-2 (#363) — «SIN MATERIAL PARA PREGUNTAS» SE SEPARA DE «NO SE PUDO».
 * Los dos eran el mismo chip rojo, y sobre un tema que sencillamente no tiene
 * nada que preguntar —una portada, un índice, una página de créditos— el rojo
 * decía dos mentiras en un renglón: que algo falló, y (con su línea «Márcalo y
 * vuelve a intentar») que reintentar sirve de algo. El estado nuevo lo dice como
 * es; el rojo se queda para el fallo de verdad, el de los cuatro intentos
 * agotados.
 *
 * Un estado desconocido devuelve `undefined` y la pantalla no pinta chip, que es
 * lo que hacía antes. */
export function textoDelChip(t, tema) {
  const estado = tema?.estado;
  if (estado === 'publicado') return t('groups.bank.temas.chip_published');
  if (estado === 'revisado') return t('groups.bank.temas.chip_reviewed');
  const llaves = {
    sin_generar: 'groups.bank.temas.state_not_generated',
    en_fila: 'groups.bank.temas.state_queued',
    generando: 'groups.bank.temas.state_generating',
    listo_para_revisar: 'groups.bank.temas.state_ready_to_review',
    sin_material: 'groups.bank.temas.state_no_material',
    no_se_pudo: 'groups.bank.temas.state_failed',
  };
  return llaves[estado] ? t(llaves[estado]) : undefined;
}

/* N1 — el banner SUAVE: hay páginas ilegibles, pero no la mitad.
 *
 * Hasta T4 este caso era SILENCIO, y el silencio escondía justo lo que explica
 * una pregunta pobre. El banner FUERTE (`pdf_damaged`) no se movió: sigue siendo
 * el de la mitad o más, con sus mismas palabras.
 *
 * SIN PLURAL, y no es un descuido: `x` e `y` son siempre ≥ 1 y el sustantivo va
 * en plural por construcción del rango. */
export function danioParcial(t, x, y) {
  return t('groups.bank.temas.damage_partial', { unreadable: x, total: y });
}

/* N3 — el aviso GLOBAL: el documento terminó de leerse mientras el profesor
 * andaba en otra pantalla. Lleva el nombre del archivo Y el del grupo porque un
 * profesor con varios grupos necesita saber a cuál ir. */
export function avisoMapaListo(t, documento, grupo) {
  return t('groups.bank.doc.ready_toast', { document: documento, group: grupo });
}

/* N4 — la etiqueta ámbar del tema cuyo rango cruza páginas ilegibles. */
export function etiquetaPaginasDanadas(t, n) {
  return t('groups.bank.temas.damaged_pages', { count: n });
}

/* N5 — el conteo de un tema terminado. */
export function conteoDePreguntas(t, n) {
  return t('groups.bank.temas.question_count', { count: n });
}

/* N7 (`Todos los documentos`) NO vive aquí: es de la pantalla de COSTOS, que es
 * de admin y de otro idioma de producto. Sus copys son literales de
 * `AdminDashboard.js` y ahí se quedan, junto a «Costos institucionales» y los
 * tres filtros — importar este módulo desde el panel de admin ataría dos
 * superficies que no tienen nada que ver. */

/* 6a — el RANGO solo. T4.5 parte la card en dos renglones exactos (el título
 * arriba, con su chip; el rango abajo, con lo contextual del estado), así que el
 * rango necesita poder pintarse sin el nombre delante. La gramática es la misma
 * y no se duplica: `renglonDeTema` compone SOBRE esta función.
 *
 * DOS LLAVES Y NO UN PLURAL DE `count`: «una página» y «un rango de páginas» son
 * dos formas distintas, no el singular y el plural de la misma. Un `_one`/`_other`
 * las forzaría a compartir plantilla y el inglés («p.» vs «pp.») lo delata.
 * Cadena vacía si faltan datos: eso no se traduce, no se pinta. */
export function rangoDePaginas(t, tema) {
  const a = tema?.start_page;
  const b = tema?.end_page;
  if (a == null || b == null) return '';
  return a === b
    ? t('groups.bank.temas.page_single', { a })
    : t('groups.bank.temas.page_range', { a, b });
}

/* T6 (firma #286) — 6b: EL RANGO SEGÚN DE DÓNDE SALIÓ EL MATERIAL.
 *
 * Tres fuentes, tres respuestas, y la tercera es el silencio:
 *
 *   · `pdf` (y todo lo anterior a T6) — páginas, exactamente como siempre.
 *   · `audio` — `min a–b`. El profesor no sabe qué es una página sintética de
 *     2,500 caracteres, pero sabe perfectamente en qué minuto de su clase pasó
 *     algo. Los minutos los calcula el SERVIDOR (`panel.rango_en_minutos`) y
 *     llegan ya resueltos en `tema.rango_min`.
 *   · `youtube` — NADA. Y es deliberado: la utilería de YouTube entrega texto
 *     plano sin marcas de tiempo, así que no hay minutos que dar; y las páginas
 *     de ese transcript son un artefacto interno del troceo, no algo que exista
 *     en el video. Enseñar «págs. 3–5» de un video sería inventarle al profesor
 *     una referencia que no puede buscar. Queda el nombre del tema, en orden de
 *     aparición, que es lo único cierto.
 *
 * La cadena vacía no se pinta (misma regla que arriba): quien llama ya sabe
 * ocultar el renglón cuando no hay rango.
 */
export function rangoDelTema(t, tema, fuente) {
  if (fuente === 'youtube') return '';
  if (fuente === 'audio') {
    const r = tema?.rango_min;
    if (!r || r.start_min == null || r.end_min == null) return '';
    return t('groups.bank.temas.min_range', { a: r.start_min, b: r.end_min });
  }
  return rangoDePaginas(t, tema);
}

/* 6 — el renglón de un tema. Una sola página no dice «págs. 4–4».
 *
 * ES COMPOSICIÓN, NO COPY: el nombre del tema es DATO del profesor y la coma (t7-3 remate R2) es
 * un separador. Por eso no tiene llave propia.
 *
 * Sigue siendo el copy firmado #118 y sigue siendo verdad: desde T4.5 se pinta
 * como el TÍTULO del nombre recortado —o sea, lo que el profesor lee entero
 * cuando el nombre no cabe— en vez de como el renglón único de la card. */
export function renglonDeTema(t, tema, fuente) {
  const nombre = tema?.nombre || '';
  // T6: `fuente` es opcional y su ausencia significa `pdf`, así que los llamadores
  // que no la pasan siguen leyendo exactamente lo que leían.
  const rango = rangoDelTema(t, tema, fuente);
  return rango ? `${nombre}, ${rango}` : nombre;
}

/* EL LETRERO DE PAQUETE VIVIA AQUI — RETIRADO EN T5 (firma #122).
 *
 * `letreroDePaquete(paquete)` componia hasta tres renglones a partir del resumen
 * del ultimo paquete cerrado, y abria la pantalla de temas con ellos. T4.5 lo
 * arreglo dos veces —el paquete que cerraba mudo, y el letrero que nacia tres
 * pantallas por debajo de donde el profesor miraba— y las dos veces el arreglo
 * dejo intacto el defecto de FONDO: un letrero PERSISTENTE describe una foto
 * vieja. Al volver al dia siguiente seguia anunciando la generacion de ayer como
 * si acabara de ocurrir.
 *
 * QUE LO SUSTITUYE: `lib/bannersDeTema.js` + los banners C1/C2/C3 de
 * `lib/curacionCopys.js`. Un aviso MOMENTANEO por tema, en el instante en que SU
 * tema termina, que se va solo a los ~6 s. Quien llega tarde no ve nada — y no
 * hace falta: los chips por tema cuentan la historia entera sin caducar.
 *
 * Con el se fue el campo `paquete` de la respuesta de `/temas`. El `paquete_id`
 * POR TEMA se queda: es la mitad de la clave con la que el diff distingue dos
 * corridas del mismo tema.
 */

/* Los temas que se pueden palomear: los que no tienen trabajo vivo. */
export const ESTADOS_OCUPADOS = ['en_fila', 'generando'];

export function sePuedeMarcar(tema) {
  return !ESTADOS_OCUPADOS.includes(tema?.estado);
}

/* Un documento tiene pantalla de temas SÓLO si viene del carril institucional.
 *
 * Los de antes del flip (`produccion_v1`, y los dos que existen en producción)
 * no tienen mapa ni temas: su pantalla contestaría 404, y la pantalla pintaría
 * «No se pudo procesar este documento. Sube el archivo de nuevo» sobre un
 * documento que SÍ se procesó — una mentira, y encima un consejo imposible de
 * seguir, porque re-subir el mismo archivo choca con el 409 de duplicado.
 * Sin perfil en la respuesta se asume que NO, que es el lado seguro. */
export const PIPELINE_INSTITUCIONAL = 'pipe_inst_v1';

export function tienePantallaDeTemas(doc) {
  return doc?.perfil?.pipeline === PIPELINE_INSTITUCIONAL;
}
